/**
 * 票链云 — 统一 API 客户端
 *
 * 约定（与后端 http/response.ts 对齐）：
 *   1. 所有接口返回 `{ code, message, data, traceId }`，业务成败只看 `code === 0`，
 *      前端不再同时处理 HTTP 状态码与业务码两套逻辑。
 *   2. 请求自动带上 `Authorization: Bearer <token>`。
 *   3. 每次请求生成 `X-Trace-Id`，出错时把它连同样例一起展示给用户，
 *      技术实施组可凭该 ID 在后端日志与 provider_call_log 里直接定位。
 *   4. 收到 401 / 40100 时清理登录态并跳回登录页（带 redirect 参数）。
 */

import axios, {
  AxiosError,
  type AxiosInstance,
  type AxiosRequestConfig,
  type AxiosResponse,
} from 'axios';
import { ApiCode, type ApiResponse, type PageResult } from '@ilc/shared';
import { feedback } from '@/utils/feedback';

/* ==========================================================================
 * 令牌存取
 *
 * 单独放在这里而不是放 store 里，是为了避免 client ←→ store 的循环依赖：
 * store 要调接口，client 要读令牌，两边都只依赖本模块。
 * ========================================================================== */

const ACCESS_TOKEN_KEY = 'ilc.accessToken';
const REFRESH_TOKEN_KEY = 'ilc.refreshToken';

let accessToken: string | null = null;

try {
  accessToken = window.localStorage.getItem(ACCESS_TOKEN_KEY);
} catch {
  accessToken = null;
}

export function getAccessToken(): string | null {
  return accessToken;
}

export function setTokens(token: string, refresh?: string | null): void {
  accessToken = token;
  try {
    window.localStorage.setItem(ACCESS_TOKEN_KEY, token);
    if (refresh) window.localStorage.setItem(REFRESH_TOKEN_KEY, refresh);
  } catch {
    /* 隐私模式下 localStorage 不可写，忽略即可，令牌仍在内存里 */
  }
}

export function getRefreshToken(): string | null {
  try {
    return window.localStorage.getItem(REFRESH_TOKEN_KEY);
  } catch {
    return null;
  }
}

export function clearTokens(): void {
  accessToken = null;
  try {
    window.localStorage.removeItem(ACCESS_TOKEN_KEY);
    window.localStorage.removeItem(REFRESH_TOKEN_KEY);
  } catch {
    /* ignore */
  }
}

/* ==========================================================================
 * 错误对象
 * ========================================================================== */

export class ApiError extends Error {
  readonly code: number;
  readonly traceId: string | null;
  readonly httpStatus: number | null;
  readonly url: string | null;
  /** 业务可降级错误：外部接口不可用、未实现等，页面可据此展示空状态而非崩溃 */
  readonly degradable: boolean;

  constructor(params: {
    message: string;
    code: number;
    traceId?: string | null;
    httpStatus?: number | null;
    url?: string | null;
  }) {
    super(params.message);
    this.name = 'ApiError';
    this.code = params.code;
    this.traceId = params.traceId ?? null;
    this.httpStatus = params.httpStatus ?? null;
    this.url = params.url ?? null;
    this.degradable =
      this.code === ApiCode.PROVIDER_UNAVAILABLE ||
      this.code === ApiCode.NOT_FOUND ||
      this.code === ApiCode.QUOTA_EXCEEDED ||
      this.httpStatus === 404 ||
      this.httpStatus === 501 ||
      this.httpStatus === 502 ||
      this.httpStatus === 503 ||
      this.httpStatus === 504;
  }
}

/** 接口尚未实现（后端并行开发中）—— 页面据此回退到演示数据 */
export function isUnimplementedError(err: unknown): boolean {
  if (err instanceof ApiError) {
    return err.code === ApiCode.NOT_FOUND || err.httpStatus === 404;
  }
  // 后端未启动时 axios 抛 ECONNREFUSED / Network Error
  if (axios.isAxiosError(err)) return !err.response;
  return false;
}

/* ==========================================================================
 * 401 处理钩子（由 auth store 注册，避免循环依赖）
 * ========================================================================== */

type UnauthorizedHandler = (reason: string) => void;
let unauthorizedHandler: UnauthorizedHandler | null = null;

export function onUnauthorized(handler: UnauthorizedHandler): void {
  unauthorizedHandler = handler;
}

/* ==========================================================================
 * axios 实例
 * ========================================================================== */

export const http: AxiosInstance = axios.create({
  baseURL: '/api',
  timeout: 30_000,
  headers: { 'Content-Type': 'application/json' },
});

function newTraceId(): string {
  try {
    if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) return crypto.randomUUID();
  } catch {
    /* ignore */
  }
  return `${Date.now().toString(16)}-${Math.random().toString(16).slice(2, 10)}`;
}

http.interceptors.request.use((config) => {
  const token = getAccessToken();
  if (token) {
    config.headers.set?.('Authorization', `Bearer ${token}`);
  }
  config.headers.set?.('X-Trace-Id', newTraceId());
  return config;
});

interface RequestMeta {
  /** 关闭自动错误提示，由调用方自行处理（页面内展示错误态） */
  silent?: boolean;
}

declare module 'axios' {
  export interface AxiosRequestConfig {
    meta?: RequestMeta;
  }
}

/** 把 axios 错误统一转成 ApiError，尽量保留后端给的 message 与 traceId */
function toApiError(error: AxiosError<ApiResponse<unknown>>): ApiError {
  const url = error.config?.url ?? null;

  if (error.response) {
    const body = error.response.data;
    const traceId =
      (body && typeof body === 'object' && 'traceId' in body ? body.traceId : null) ??
      (error.response.headers['x-trace-id'] as string | undefined) ??
      null;

    if (body && typeof body === 'object' && 'code' in body) {
      return new ApiError({
        message: body.message || `请求失败（HTTP ${error.response.status}）`,
        code: typeof body.code === 'number' ? body.code : ApiCode.INTERNAL_ERROR,
        traceId,
        httpStatus: error.response.status,
        url,
      });
    }
    return new ApiError({
      message: `请求失败（HTTP ${error.response.status}）`,
      code: ApiCode.INTERNAL_ERROR,
      traceId,
      httpStatus: error.response.status,
      url,
    });
  }

  if (error.code === 'ECONNABORTED') {
    return new ApiError({
      message: '请求超时，请稍后重试或联系技术实施组',
      code: ApiCode.PROVIDER_UNAVAILABLE,
      httpStatus: null,
      url,
    });
  }

  return new ApiError({
    message: '无法连接后端服务（http://localhost:3100），请确认服务已启动',
    code: ApiCode.PROVIDER_UNAVAILABLE,
    httpStatus: null,
    url,
  });
}

http.interceptors.response.use(
  (response: AxiosResponse<ApiResponse<unknown>>) => response,
  (error: AxiosError<ApiResponse<unknown>>) => {
    const apiError = toApiError(error);
    const silent = error.config?.meta?.silent === true;

    if (apiError.code === ApiCode.UNAUTHORIZED) {
      unauthorizedHandler?.(apiError.message);
      if (!silent) feedback.error(apiError.message);
      return Promise.reject(apiError);
    }

    if (!silent) {
      // 提示里带上 traceId，用户截图给技术组即可定位
      const suffix = apiError.traceId ? `（traceId: ${apiError.traceId.slice(0, 8)}）` : '';
      feedback.error(`${apiError.message}${suffix}`);
    }
    return Promise.reject(apiError);
  },
);

/* ==========================================================================
 * 请求包装 —— 统一解包 data
 * ========================================================================== */

async function unwrap<T>(promise: Promise<AxiosResponse<ApiResponse<T>>>): Promise<T> {
  const response = await promise;
  const body = response.data;

  // 204 / 空响应体
  if (body === null || body === undefined) return undefined as T;

  if (typeof body !== 'object' || !('code' in body)) {
    // 后端返回了非约定结构，属于契约破坏，直接暴露出来而不是静默当成功
    throw new ApiError({
      message: '后端返回结构不符合约定（缺少 code 字段）',
      code: ApiCode.INTERNAL_ERROR,
      httpStatus: response.status,
      url: response.config?.url ?? null,
    });
  }

  if (body.code !== ApiCode.OK) {
    throw new ApiError({
      message: body.message || '请求失败',
      code: body.code,
      traceId: body.traceId ?? null,
      httpStatus: response.status,
      url: response.config?.url ?? null,
    });
  }

  return body.data;
}

export interface RequestOptions extends AxiosRequestConfig {
  /** 不弹全局错误提示，由页面自行渲染错误态 */
  silent?: boolean;
}

function withMeta(options?: RequestOptions): AxiosRequestConfig {
  const { silent, ...rest } = options ?? {};
  return { ...rest, meta: { silent: silent === true } };
}

export const api = {
  get<T>(url: string, params?: unknown, options?: RequestOptions): Promise<T> {
    return unwrap<T>(http.get(url, withMeta({ ...options, params })));
  },
  post<T>(url: string, body?: unknown, options?: RequestOptions): Promise<T> {
    return unwrap<T>(http.post(url, body, withMeta(options)));
  },
  put<T>(url: string, body?: unknown, options?: RequestOptions): Promise<T> {
    return unwrap<T>(http.put(url, body, withMeta(options)));
  },
  patch<T>(url: string, body?: unknown, options?: RequestOptions): Promise<T> {
    return unwrap<T>(http.patch(url, body, withMeta(options)));
  },
  delete<T>(url: string, options?: RequestOptions): Promise<T> {
    return unwrap<T>(http.delete(url, withMeta(options)));
  },
  /** multipart 上传 —— 不设置 Content-Type，交给浏览器带 boundary */
  upload<T>(
    url: string,
    formData: FormData,
    options?: RequestOptions & { onProgress?: (percent: number) => void },
  ): Promise<T> {
    const { onProgress, ...rest } = options ?? {};
    return unwrap<T>(
      http.post(url, formData, {
        ...withMeta(rest),
        headers: { 'Content-Type': undefined },
        timeout: 120_000,
        onUploadProgress: (e) => {
          if (onProgress && e.total) onProgress(Math.round((e.loaded / e.total) * 100));
        },
      }),
    );
  },
};

/** 统一分页请求参数清理：去掉空值，避免后端把空字符串当条件 */
export function cleanQuery<T extends object>(query: T): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(query)) {
    if (value === undefined || value === null || value === '') continue;
    out[key] = value;
  }
  return out;
}

/** 空分页结果 —— 接口不可用时页面展示空状态而不是崩溃 */
export function emptyPage<T>(pageSize = 20): PageResult<T> {
  return { list: [], total: 0, page: 1, pageSize };
}
